import { UseCase } from "@/services/global-services/models";
import { useLocaleStore } from "@/stores";
import { Dictionary } from "@/utils/Dictionary";
import {
    ArrowDownIcon,
    ArrowUpIcon,
    ChevronDownIcon,
    EyeIcon,
    EyeSlashIcon,
    MinusIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import { Box, IconButton, Typography } from "@mui/material";
import clsx from "clsx";
import { groupBy } from "lodash";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { moveRowDown, moveRowUp, removeRow, setRowVisibility } from "../../../../context/DataTableReducer";
import useDataTableContext from "../../../../context/useDataTableContext";
import { DataTableRow, DataType, Metadata } from "../../../../Models";
import StyledTableCell from "../StyledTableCell";
import EditableCell from "./Components/EditableCell";
import ReadOnlyCell from "./Components/ReadOnlyCell";

const addRowDividers = () => {
    const wrappers = document.querySelectorAll(".usecase-list") as NodeListOf<HTMLDivElement>;
    if (wrappers.length === 0) return;

    wrappers.forEach((wrapper) => {
        const children = Array.from(wrapper.children).filter((child) => !child.classList.contains("row-divider"));
        const rowTops = new Map();

        // Remove old dividers
        wrapper.querySelectorAll(".row-divider").forEach((div) => div.remove());

        children.forEach((el) => {
            const top = (el as HTMLElement).offsetTop;
            if (!rowTops.has(top)) {
                rowTops.set(top, []);
            }
            rowTops.get(top).push(el);
        });

        const rows = Array.from(rowTops.values());

        for (let i = 1; i < rows.length; i++) {
            const prevRow = rows[i - 1];
            const lastEl = prevRow[prevRow.length - 1];

            const divider = document.createElement("div");
            divider.className = "absolute left-0 right-0 h-[0.5px] bg-[#37b89f] row-divider";
            divider.style.top = lastEl.offsetTop + lastEl.offsetHeight + "px";

            wrapper.appendChild(divider);
        }
    });
};

const DataTableBody = () => {
    const { state, dispatch } = useDataTableContext();
    const regions = useLocaleStore((state) => state.availableRegions);
    const activeRegion = useLocaleStore((state) => state.activeRegion);

    const {
        columns,
        rows,
        readOnly,
        dataSource,
        showGroupHeaderRows,
        hasPropertyLevel,
        useCases,
        onFilterRow,
        onFilterColumn,
        onFilterUseCase,
    } = state;
    const [infoVisible, setInfoVisible] = useState<Array<string>>([]);
    const [useCaseInfoVisible, setUseCaseInfoVisible] = useState<UseCase>();

    const { t } = useTranslation(["datatable"]);

    useEffect(() => {
        addRowDividers();
    });

    useEffect(() => {
        state.onChange({ rows: state.rows, footnotes: state.footnotes, caption: state.caption || "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.rows, state.footnotes, state.caption]);

    let isOdd = true;
    let rendered = 0;

    const renderGroupTitle = (metadata: Metadata) => {
        if (hasPropertyLevel) {
            if (metadata?.groupCode === "A") return t("property-level.object", { ns: "datatable" });

            if (metadata?.groupCode === "m") return t("property-level.material", { ns: "datatable" });
        }

        return metadata?.groupName || metadata?.groupCode;
    };

    const renderGroup = (groupItems: Array<DataTableRow>) => {
        if (readOnly) {
            const visibleItems = groupItems.filter((row) => !onFilterRow(row));
            if (visibleItems.length === 0) return null;
        }

        let visibleColumnCount = groupItems[0].cells
            .map((_, columnId) => {
                const column = columns[columnId];

                if (onFilterColumn && onFilterColumn(column)) return 0;
                return 1;
            })
            .filter((x) => x === 1).length;

        if (!readOnly) visibleColumnCount += 1; // Add one for the action column

        return (
            <>
                {showGroupHeaderRows && groupItems[0].metadata?.groupCode && (
                    <tr className="bg-primary-300 text=[#252828]">
                        <td className="border-l border-b border-r border-primary-300" colSpan={visibleColumnCount}>
                            <div className="inline-block py-3 px-2.5">
                                {renderGroupTitle(groupItems[0].metadata as Metadata)}
                            </div>
                        </td>
                    </tr>
                )}

                {groupItems.map((row, rowIndex) => {
                    const isEmpty = row.cells.every((x) => (x.value ? x.value.length === 0 : true));
                    if (readOnly && (isEmpty || (onFilterRow && onFilterRow(row)))) return null;

                    const isVisible = row.metadata?.visible !== false;
                    const isInfoVisible = infoVisible.indexOf(row.id) > -1;
                    const hasInfo =
                        row.metadata?.description !== undefined && (row.metadata?.description as string)?.length > 0;
                    const hasPredefinedValues =
                        row.metadata?.predefinedValues !== undefined &&
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (row.metadata.predefinedValues as any[])?.length > 0;

                    const toggleVisibility = () => {
                        if (isInfoVisible) setInfoVisible(infoVisible.filter((x) => x !== row.id));
                        else setInfoVisible([...infoVisible, row.id]);
                    };

                    if (readOnly && !isVisible) return null;

                    isOdd = !isOdd;
                    rendered++;

                    return (
                        <Fragment key={rowIndex}>
                            <tr data-table-row={row.id} className={clsx(["", isOdd && "bg-[#F7FAFF]"])}>
                                {row.cells.map((cell, cellIndex) => {
                                    if (readOnly)
                                        return (
                                            <ReadOnlyCell
                                                key={cell.id}
                                                data-table-row={row.id}
                                                data-table-cell={cell.id}
                                                rowId={row.id}
                                                cell={cell}
                                                hasInfo={hasInfo && cellIndex === 0}
                                                isInfoVisible={isInfoVisible}
                                                toggleInfo={toggleVisibility}
                                            />
                                        );

                                    return (
                                        <EditableCell
                                            key={cell.id}
                                            data-table-row={row.id}
                                            data-table-cell={cell.id}
                                            row={row.id}
                                            cell={cell}
                                            visible={isVisible}
                                            readOnly={false}
                                        />
                                    );
                                })}

                                {!readOnly && (
                                    <td className="border-l border-r border-b border-[#E5E5E5] group-last:first:rounded-bl-lg group-last:last:rounded-br-lg">
                                        <div className="flex">
                                            <IconButton
                                                size="small"
                                                tabIndex={-1}
                                                onClick={() => dispatch(moveRowUp(row.id))}
                                                sx={{ ml: 1 }}
                                                disabled={rowIndex === 0 || Boolean(dataSource)}
                                            >
                                                <ArrowUpIcon className="h-5 w-5" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                className="grow-0 shrink-0"
                                                tabIndex={-1}
                                                onClick={() => dispatch(moveRowDown(row.id))}
                                                sx={{ ml: 1, mr: 1 }}
                                                disabled={rowIndex === rows.length - 1 || Boolean(dataSource)}
                                            >
                                                <ArrowDownIcon className="h-5 w-5" />
                                            </IconButton>
                                            {dataSource ? (
                                                <IconButton
                                                    size="small"
                                                    tabIndex={-1}
                                                    onClick={() => dispatch(setRowVisibility(row.id, !isVisible))}
                                                    sx={{ mr: 1, ml: "auto" }}
                                                >
                                                    {isVisible ? (
                                                        <EyeIcon className="h-5 w-5" />
                                                    ) : (
                                                        <EyeSlashIcon className="h-5 w-5" />
                                                    )}
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    tabIndex={-1}
                                                    onClick={() => dispatch(removeRow(row.id))}
                                                    sx={{ mr: 1, ml: "auto" }}
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </IconButton>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>

                            {readOnly && (hasInfo || hasPredefinedValues) && isVisible && isInfoVisible && (
                                <tr className={clsx(["group bg-primary-100 text-primary-500"])}>
                                    <td
                                        colSpan={visibleColumnCount}
                                        className={clsx([
                                            "relative py-3 px-2.5 border border-primary-400 group-last:rounded-b-lg",
                                        ])}
                                    >
                                        <div className="-top-[9px] left-10 absolute h-4 w-4 bg-primary-100 border-l border-t border-primary-400 rotate-45" />
                                        <div className="flex flex-col gap-2 font-medium">
                                            {hasInfo && row.metadata?.description}
                                            {hasPredefinedValues && (
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex ">
                                                        <span className="font-bold">
                                                            {t("property-information.predefined", { ns: "datatable" })}
                                                        </span>
                                                        <span className="text-secondary-500/70 ml-4 font-medium">
                                                            {t("property-information.predefined-tooltip", {
                                                                ns: "datatable",
                                                            })}
                                                        </span>
                                                    </div>
                                                    <span className="block">
                                                        {(
                                                            row.metadata!.predefinedValues as Array<{
                                                                name: string;
                                                                code: string;
                                                            }>
                                                        )
                                                            .map((x) => x.name)
                                                            .join(", ")}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    );
                })}
            </>
        );
    };

    const renderRows = () => {
        const rows = [...state.rows];

        if (columns.some((x) => x.dataType === DataType.BOOLEAN) && readOnly) {
            const propertyGroups: Dictionary<DataTableRow[]> = {};

            rows.forEach((row) => {
                const splitId = row.id.split("_");
                const propertyId = splitId[0];

                if (!propertyGroups[propertyId]) {
                    propertyGroups[propertyId] = [];
                }

                propertyGroups[propertyId].push(row);
            });

            const propertyCodes = Object.keys(propertyGroups);
            const newRows: Array<DataTableRow> = [];

            for (let i = 0; i < propertyCodes.length; i++) {
                const propertyCode = propertyCodes[i];
                const propertyRows = propertyGroups[propertyCode].filter((x) => {
                    if (!x.metadata?.useCases) return false;

                    const useCaseId = (x.metadata?.useCases as Array<string>)[0];
                    const useCase = useCases?.find((x) => x.id === useCaseId);
                    if (!useCase) return false;

                    const potentialRegionCode = useCase.code.slice(-2).toLowerCase();
                    let containsRegionCode = false;
                    if (regions && regions.length > 0) {
                        containsRegionCode = regions.some((x) => x.regionCode.toLowerCase() === potentialRegionCode);
                        const activeRegionCode = regions.find((x) => x.slug === activeRegion)!.regionCode.toLowerCase();

                        if (containsRegionCode && potentialRegionCode !== activeRegionCode) {
                            return false;
                        }
                    }

                    return !onFilterUseCase(useCase);
                });

                if (propertyRows.length === 0) continue;

                const firstPropertyRow = JSON.parse(JSON.stringify(propertyRows[0]));

                const mergedRow = { ...firstPropertyRow };
                for (let j = 1; j < propertyRows.length; j++) {
                    const row = JSON.parse(JSON.stringify(propertyRows[j]));

                    for (let k = 0; k < row.cells.length; k++) {
                        const cell = row.cells[k];
                        const column = columns[k];
                        const mergedCell = mergedRow.cells[k];

                        if (!cell.metadata) continue;

                        ((cell.metadata.disciplines as Array<string>) ?? []).forEach((discipline) => {
                            if (column.dataType === DataType.BOOLEAN) {
                                if (!mergedCell.metadata) mergedCell.metadata = {};

                                if (!mergedCell.metadata.disciplines) mergedCell.metadata.disciplines = [];
                                if (!(mergedCell.metadata.disciplines as Array<string>).includes(discipline)) {
                                    (mergedCell.metadata.disciplines as Array<string>).push(discipline);
                                }
                            }
                        });
                    }

                    // Metadata.disciplines
                    if (!row.metadata) continue;

                    ((row.metadata.disciplines as Array<string>) ?? []).forEach((discipline) => {
                        if (!mergedRow.metadata) {
                            mergedRow.metadata = {};
                        }

                        if (!mergedRow.metadata?.disciplines) {
                            mergedRow.metadata.disciplines = [];
                        }

                        if (!(mergedRow.metadata.disciplines as Array<string>).includes(discipline)) {
                            (mergedRow.metadata.disciplines as Array<string>).push(discipline);
                        }
                    });

                    // Metadata.useCases
                    ((row.metadata.useCases as Array<string>) ?? []).forEach((useCase) => {
                        if (!mergedRow.metadata) {
                            mergedRow.metadata = {};
                        }

                        if (!mergedRow.metadata?.useCases) {
                            mergedRow.metadata.useCases = [];
                        }

                        if (!(mergedRow.metadata.useCases as Array<string>).includes(useCase)) {
                            (mergedRow.metadata.useCases as Array<string>).push(useCase);
                        }
                    });
                }

                newRows.push(mergedRow);
            }

            if (newRows.length === 0) return null;

            const attachedUseCases = new Set<string>(
                newRows.filter((x) => !onFilterRow(x)).flatMap((x) => x.metadata?.useCases as Array<string>)
            );
            let visibleColumnCount = newRows[0].cells
                .map((_, columnId) => {
                    const column = columns[columnId];

                    if (onFilterColumn && onFilterColumn(column)) return 0;
                    return 1;
                })
                .filter((x) => x === 1).length;

            if (!readOnly) visibleColumnCount += 1; // Add one for the action column

            const groups = groupBy(newRows, (x) => x.metadata?.groupCode);
            const isInfoVisible = useCaseInfoVisible !== undefined;

            return (
                <>
                    <tbody className="bg-white text-black font-medium last:[&>tr:last-child>th:first-child]:rounded-bl-lg last:[&>tr:last-child>td:last-child]:rounded-br-lg">
                        <tr className="bg-[#8cd6c3] text-[#060908]">
                            <td
                                className={clsx([
                                    "border-l border-r border-[#37b89f]",
                                    isInfoVisible ? "border-b-0" : "border-b",
                                ])}
                                colSpan={visibleColumnCount}
                            >
                                <div className="usecase-list flex flex-wrap relative">
                                    {useCases
                                        ?.filter((x) => attachedUseCases.has(x.id))
                                        ?.map((useCase) => {
                                            if (onFilterUseCase(useCase)) return undefined;

                                            if (!activeRegion) return undefined;

                                            const potentialRegionCode = useCase.code.slice(-2).toLowerCase();
                                            let containsRegionCode = false;
                                            if (regions && regions.length > 0) {
                                                containsRegionCode = regions.some(
                                                    (x) => x.regionCode.toLowerCase() === potentialRegionCode
                                                );
                                                const activeRegionCode = regions
                                                    .find((x) => x.slug === activeRegion)!
                                                    .regionCode.toLowerCase();
                                                if (containsRegionCode && potentialRegionCode !== activeRegionCode) {
                                                    return undefined;
                                                }
                                            }

                                            return useCase;
                                        })
                                        .filter((x) => x !== undefined)
                                        .map((useCase) => {
                                            const toggled = useCaseInfoVisible?.id === useCase.id;
                                            const toggleVisibility = () => {
                                                if (useCaseInfoVisible?.id === useCase.id)
                                                    setUseCaseInfoVisible(undefined);
                                                else setUseCaseInfoVisible(useCase);
                                            };

                                            return (
                                                <div
                                                    key={useCase.id}
                                                    onClick={() => useCase.description && toggleVisibility()}
                                                    className={clsx([
                                                        "py-3 px-2.5 border-r border-[#37b89f] whitespace-nowrap cursor-pointer hover:backdrop-brightness-95",
                                                        useCaseInfoVisible?.id === useCase.id && "font-bold",
                                                    ])}
                                                >
                                                    <div className="flex items-center">
                                                        <span className="mr-5">{useCase.name ?? useCase.code}</span>
                                                        {toggled ? (
                                                            <MinusIcon
                                                                className="w-5 h-5 text-primary-500 cursor-pointer"
                                                                onClick={toggleVisibility}
                                                            />
                                                        ) : (
                                                            <ChevronDownIcon
                                                                className="w-5 h-5 text-primary-500 cursor-pointer"
                                                                onClick={toggleVisibility}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </td>
                        </tr>

                        {isInfoVisible && (
                            <tr className="bg-primary-100 text-primary-500">
                                <td
                                    className="relative py-3 px-2.5 border-t border-l border-r border-b border-primary-400"
                                    colSpan={visibleColumnCount}
                                >
                                    {/* <div className="-top-[9px] left-10 absolute h-4 w-4 bg-primary-100 border-l border-t border-primary-400 rotate-45" /> */}
                                    <div className="flex flex-col gap-2 font-medium">
                                        <span className="block font-semibold">{useCaseInfoVisible?.name}</span>
                                        <span>{useCaseInfoVisible?.description}</span>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {Object.keys(groups).map((group) => (
                            <Fragment key={group}>{renderGroup(groups[group])}</Fragment>
                        ))}
                    </tbody>
                </>
            );
        } else if (columns.some((x) => x.dataType === DataType.BOOLEAN)) {
            // Table stuff for administration section
            const useCaseGroups: Dictionary<Array<DataTableRow>> = {};
            rows.forEach((row) => {
                const useCases = (row.metadata?.useCases as string[]) || [];
                useCases.forEach((useCase) => {
                    if (!useCaseGroups[useCase]) {
                        useCaseGroups[useCase] = [];
                    }
                    useCaseGroups[useCase].push(row);
                });
            });

            return (
                <>
                    {Object.keys(useCaseGroups)
                        .sort((a, b) => {
                            const useCaseA = useCases?.find((x) => x.id === a)?.code || "UC 9999";
                            const useCaseB = useCases?.find((x) => x.id === b)?.code || "UC 9999";

                            return useCaseA.localeCompare(useCaseB, undefined, { numeric: true });
                        })
                        .map((id) => {
                            const useCase = useCases?.find((x) => x.id === id);
                            if (!useCase || onFilterUseCase(useCase)) return null;

                            const groups = groupBy(useCaseGroups[id], (x) => x.metadata?.groupCode);

                            return (
                                <tbody
                                    key={id}
                                    className="last:[&>tr:last-child>th:first-child]:rounded-bl-lg last:[&>tr:last-child>td:last-child]:rounded-br-lg"
                                >
                                    <tr>
                                        <StyledTableCell
                                            colSpan={columns.length + 1}
                                            sx={{
                                                backgroundColor: "#5EC6AB",
                                                color: (theme) => theme.palette.common.white,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    width: 1,
                                                    maxWidth: {
                                                        xs: "calc(100vw - 16px - 32px + 1px)",
                                                        lg: "calc(80vw - 16px - 32px + 1px)",
                                                    },
                                                    position: "sticky",
                                                    left: 0,
                                                    right: 0,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ m: 2, fontWeight: 700 }}>
                                                    {useCase.name ?? useCase.code}
                                                    {/* {useCase.code} {useCase.name && ` - ${useCase.name}`} */}
                                                </Typography>
                                            </Box>
                                        </StyledTableCell>
                                    </tr>

                                    {Object.keys(groups).map((group) => (
                                        <Fragment key={group}>{renderGroup(groups[group])}</Fragment>
                                    ))}
                                </tbody>
                            );
                        })}
                </>
            );
        }

        const groups = groupBy(rows, (x) => x.metadata?.groupCode);
        return (
            <>
                {Object.keys(groups).map((group, index) => (
                    <tbody
                        key={(groups[group][0].metadata?.groupCode as string) || `unidentified-group-${index}`}
                        className="last:[&>tr:last-child>th:first-child]:rounded-bl-lg last:[&>tr:last-child>td:last-child]:rounded-br-lg"
                    >
                        {renderGroup(groups[group])}
                    </tbody>
                ))}
            </>
        );
    };

    return (
        <>
            {renderRows()}

            {rendered === 0 && (
                <tbody>
                    <tr>
                        <td
                            colSpan={columns.length + 1}
                            className="h-[100px] font-bold text-center m-2 border-l border-r border-b border-[#E5E5E5] rounded-b-lg"
                        >
                            {t("datatable.empty", { ns: "elements" })}
                        </td>
                    </tr>
                </tbody>
            )}
        </>
    );
};

export default DataTableBody;
